
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import FormInput from "@/components/auth/common/FormInput";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const formSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  preferredCounty: z.string().min(1, "Please select a preferred county"),
  hasExperience: z.enum(["yes", "no"]),
  legalRightToWork: z.enum(["yes", "no"], {
    required_error: "Please indicate if you have the legal right to work in Ireland",
  }),
  cvUrl: z.string().optional(),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must agree to the privacy policy",
  }),
});

type FormData = z.infer<typeof formSchema>;

interface JobApplicationFormProps {
  counties: { county: string; county_slug: string }[];
}

export function JobApplicationForm({ counties }: JobApplicationFormProps) {
  const { toast } = useToast();
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      preferredCounty: "",
      hasExperience: "no",
      legalRightToWork: undefined,
      consent: false,
    },
  });

  const onSubmit = async (data: FormData) => {
    console.log("Form submitted:", data);
    
    toast({
      title: "Application submitted",
      description: "Thanks for your interest! We'll be in touch soon.",
    });
    
    form.reset();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormInput
          control={form.control}
          name="fullName"
          label="Full Name"
          required
        />

        <FormInput
          control={form.control}
          name="email"
          label="Email Address"
          required
        />

        <FormInput
          control={form.control}
          name="phone"
          label="Phone Number"
          required
        />

        <div className="space-y-3">
          <Label>Preferred County</Label>
          <Select 
            onValueChange={(value) => form.setValue("preferredCounty", value)}
            value={form.watch("preferredCounty")}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a county" />
            </SelectTrigger>
            <SelectContent>
              {counties.map((county) => (
                <SelectItem key={county.county_slug} value={county.county_slug}>
                  {county.county}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <Label>Do you have experience in elderly care?</Label>
          <RadioGroup
            onValueChange={(value) => form.setValue("hasExperience", value as "yes" | "no")}
            defaultValue="no"
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="yes" />
              <Label htmlFor="yes">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="no" />
              <Label htmlFor="no">No</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-3">
          <Label required>Do you have the legal right to work in Ireland?</Label>
          <RadioGroup
            onValueChange={(value) => form.setValue("legalRightToWork", value as "yes" | "no")}
            value={form.watch("legalRightToWork") || ""}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="legal-right-yes" />
              <Label htmlFor="legal-right-yes">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="legal-right-no" />
              <Label htmlFor="legal-right-no">No</Label>
            </div>
          </RadioGroup>
          {form.formState.errors.legalRightToWork && (
            <p className="text-destructive text-sm mt-1">
              {String(form.formState.errors.legalRightToWork.message)}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label>Upload your CV (Optional)</Label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                // Handle file upload here
                console.log("File selected:", e.target.files[0]);
              }
            }}
            className="w-full cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm file:mr-4 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-sm file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
          />
        </div>

        <Button type="submit" className="w-full">
          Submit Application
        </Button>

        <p className="text-sm text-gray-500 mt-4">
          By submitting this form, you agree to our{" "}
          <a href="/legal/privacy-policy" className="text-purple-600 hover:underline">
            Privacy Policy
          </a>
          . We'll only use your information to process your job application.
        </p>
      </form>
    </Form>
  );
}

